import { Box, Tab, Tabs } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { ReactElement } from 'react';
import { UserPermission, UserRole } from '@synergy/models';
import { appendFilterParams } from '@synergy/components';
import { hasPermission, hasRole } from '@synergy/utils';
import { useLocation, useNavigate } from 'react-router-dom';

import { DashboardTabs } from '../model/dashboard-tabs';
import { STATUS_OPTIONS } from '../../../common/components/filter/document-status-options';
import { searchResultCountSelector } from '../store/dashboard-selectors';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { userSelector } from '../../../common/selectors/user-selectors';

const c = tokens.color;

interface TabDef {
    value: DashboardTabs;
    label: string;
    countKey: keyof ReturnType<typeof searchResultCountSelector>;
}

const TAB_DEFS: TabDef[] = [
    { value: DashboardTabs.SalesOrders, label: 'app.navigation.salesOrders', countKey: 'salesOrders' },
    { value: DashboardTabs.Inquiries, label: 'app.navigation.inquiries', countKey: 'inquiries' },
    { value: DashboardTabs.PurchaseOrders, label: 'app.navigation.purchaseOrders', countKey: 'purchaseOrders' },
    { value: DashboardTabs.Demands, label: 'app.navigation.demands', countKey: 'demands' },
    { value: DashboardTabs.Invoices, label: 'app.navigation.invoices', countKey: 'invoices' },
];

export const PaloDashboardOverviewTabs = (): ReactElement => {
    const navigate = useNavigate();
    const location = useLocation();
    const counts = useAppSelector(searchResultCountSelector);
    const user = useAppSelector(userSelector);

    const isAllowed = (tab: DashboardTabs): boolean => {
        switch (tab) {
            case DashboardTabs.SalesOrders:
                return hasPermission(UserPermission.SalesOrders, user);
            case DashboardTabs.Inquiries:
                return hasPermission(UserPermission.Inquiries, user);
            case DashboardTabs.PurchaseOrders:
                return hasPermission(UserPermission.PurchaseOrders, user);
            case DashboardTabs.Demands:
                return hasPermission(UserPermission.Demands, user);
            case DashboardTabs.Invoices:
                return hasRole([UserRole.Admin, UserRole.SuperAdmin, UserRole.Accounting], user);
            default:
                return false;
        }
    };

    const visibleTabs = TAB_DEFS.filter(tab => isAllowed(tab.value));
    const activeSegment = location.pathname.split('/').pop() ?? '';
    const activeTab = visibleTabs.find(tab => tab.value === activeSegment)?.value ?? visibleTabs[0]?.value;

    /**
     * The filters carry over to the next tab, the sorting does not: it names a
     * column of the tab it was set on, and the next tab either does not show
     * that column or cannot sort by it at all.
     *
     * A status carries over only where the next tab's kind has it: "Abgerechnet"
     * means nothing to an invoice. Left standing, the chip would name a filter
     * the server ignores for that kind (#2340).
     */
    const tabSearch = (tab: DashboardTabs): string => {
        const params = new URLSearchParams(location.search);
        const kept = [...params.getAll('filter[status]'), ...params.getAll('filter[status][]')].filter(status =>
            STATUS_OPTIONS[tab].statuses.includes(status)
        );

        params.delete('sort');
        params.delete('order');
        params.delete('filter[status]');
        params.delete('filter[status][]');
        appendFilterParams(
            params,
            kept.map(status => ['status', status] as [string, string])
        );

        return params.toString();
    };

    const badge = (count: number, active: boolean): ReactElement => (
        <Box
            component="span"
            sx={{
                fontSize: '10.5px',
                fontWeight: 600,
                padding: '0 6px',
                borderRadius: '9px',
                fontVariantNumeric: 'tabular-nums',
                backgroundColor: active ? c.accentSoft : c.neutralSoft,
                color: active ? c.accentHi : c.ink2,
            }}
        >
            {count}
        </Box>
    );

    return (
        <PaloThemeProvider>
            <Tabs
                value={activeTab ?? false}
                onChange={(_, tab) => navigate({ pathname: `/dashboard/${tab}`, search: tabSearch(tab) })}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: `1px solid ${c.line}` }}
            >
                {visibleTabs.map(tab => (
                    <Tab
                        key={tab.value}
                        value={tab.value}
                        label={
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                                {badge(counts[tab.countKey], tab.value === activeTab)}
                                <FormattedMessage id={tab.label} />
                            </Box>
                        }
                    />
                ))}
            </Tabs>
        </PaloThemeProvider>
    );
};
